import React, { useEffect, useMemo, useState } from 'react';
import { BadgeAchievementProps } from './BadgeAchievement.props';
import { Box, Stack, Typography } from '@mui/material';
import './BadgeAchievement.css';
import { DataTables } from '../DataTables';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { formatNumber, getCurrentCellStore, getUserName } from '../../utils/util';
import { UserAvatarLetters } from '../UserAvatarLetters';
import { images } from '../../constants/images';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';

import {
  getBadgeAchievementListEvents,
  getBadgeAchievementListEventsDataSelector,
  IDataBadgeAchievement,
  IDataBadgeAchievementList,
  IGroupBadgeAchievementList,
  removeBadgeAchievement
} from '../../redux/getBadgeAchievementListSilce';
import { LoadingPage } from '../LoadingPage';
import BadgeSliderAchievement from '../BadgeSlider/BadgeSliderAchievement';
import { CustomChip } from '../CustomChip';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

interface badgeAchievementRow extends IDataBadgeAchievementList {
  id: number;
}

interface groupBadgeAchievementRow extends IGroupBadgeAchievementList {
  id: number;
}

const BadgeAchievement: React.FC<BadgeAchievementProps> = ({ cellId, isAttribute = false }) => {
  const [badgeAchievementRows, setBadgeAchievementRows] = useState<
    badgeAchievementRow[] | groupBadgeAchievementRow[]
  >([]);
  const [totalRows, setTotalRows] = useState(0);
  const { dataBadgeAchievement, isLoading } = useAppSelector(
    getBadgeAchievementListEventsDataSelector
  );
  const userCurrentCellStore = getCurrentCellStore();
  const dispatch = useAppDispatch();
  const [selectedRow, setSelectedRow] = useState<any[]>([]);

  const DEFAULT_PARAMS = {
    cell_id:cellId,
    offset_rankings: 1,
    offset_groups: 1,
    limit_rankings: 10,
    limit_groups: 10
  };
  const columns: GridColDef[] = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: 'rank',
        headerName: '',
        sortable: false,
        width: 20,
        align: 'center',
        headerClassName: 'data-table-header-cell',
        renderCell: (params: GridRenderCellParams) => {
          return <p>#{params.row.rank}</p>;
        }
      },
      {
        field: 'user_email',
        headerName: 'Learners',
        flex: 4,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                minHeight: '68px'
              }}>
              {getUserName(params.row) ? (
                <UserAvatarLetters
                  classNames={'avatar-list-badge-achievement'}
                  name={getUserName(params.row)}
                />
              ) : (
                <div className="profile-avatar">
                  <img className="profile-picture" src={images.NoAvatar} alt="avatar" />
                </div>
              )}
              <Stack sx={{ ml: '10px', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                <Typography className={'name-cell row-selected'}>
                  {params.row.first_name} {params.row.last_name}
                </Typography>
                {params.row.learner_id === (userCurrentCellStore && userCurrentCellStore.student_id) && (
                  <CustomChip
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontFamily: 'Montserrat',
                          fontSize: 14
                        }}>
                        Your Position
                        <AccountCircleIcon sx={{ fontSize: 24, color: '#193051' }} />
                      </Box>
                    }
                    className={`custom-chip position btn-your-position`}
                  />
                )}
              </Stack>
            </Box>
          );
        }
      },
      {
        field: 'badges_acquired',
        headerName: 'Acquired',
        sortable: false,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params: GridRenderCellParams) => params.value
      },
      {
        field: 'badges_remaining',
        headerName: 'Remaining',
        flex: 1,
        sortable: false,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params: GridRenderCellParams) => params.value
      },
      {
        field: 'recent_badges',
        headerName: 'Achievements',
        sortable: false,
        flex: 4,
        align: 'left',
        headerAlign: 'left',
        cellClassName: 'badges-cell',
        renderCell: (params: GridRenderCellParams) => (
          <BadgeSliderAchievement badgeList={params.row.recent_badges} />
        )
      },
      {
        field: 'total_points',
        headerName: 'Total Points',
        sortable: false,
        flex: 0.75,
        align: 'right',
        headerClassName: 'header-sort-right',
        sortingOrder: ['asc', 'desc'],
        renderCell: (params: GridRenderCellParams) => formatNumber(params.value)
      }
    ];
    if (isAttribute) {
      return [
        {
          field: 'rank',
          headerName: '',
          sortable: false,
          width: 20,
          align: 'center',
          headerClassName: 'data-table-header-cell',
          renderCell: (params: GridRenderCellParams) => {
            return <p>#{params.row.rank}</p>;
          }
        },
        {
          field: 'group',
          headerName: 'Group',
          flex: 4,
          sortable: false,
          renderCell: (params: GridRenderCellParams) => {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '68px',
                  gap: '10px'
                }}>
                {params && params.value ? (
                  <UserAvatarLetters
                    classNames={'avatar-list-badge-achievement'}
                    name={params.value}
                  />
                ) : (
                  <div className="profile-avatar">
                    <img className="profile-picture" src={images.NoAvatar} alt="avatar" />
                  </div>
                )}
                <Stack>
                  <Typography className="name-cell row-selected">
                    {params.value}
                  </Typography>
                </Stack>
                { params.row.has_group && (
                  <CustomChip
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontFamily: 'Montserrat',
                          fontSize: 14
                        }}>
                        Your Group
                        <AccountCircleIcon sx={{ fontSize: 24, color: '#193051' }} />
                      </Box>
                    }
                    className={`custom-chip position btn-your-group`}
                  />
                )}
              </Box>
            );
          }
        },
        {
          field: 'badges_acquired',
          headerName: 'Acquired',
          sortable: false,
          flex: 1.5,
          align: 'left',
          headerAlign: 'left',
          renderCell: (params: GridRenderCellParams) => params.value
        },
        {
          field: 'badges_remaining',
          headerName: 'Remaining',
          flex: 1.5,
          sortable: false,
          align: 'left',
          headerAlign: 'left',
          renderCell: (params: GridRenderCellParams) => params.value
        },
        {
          field: 'recent_badges',
          headerName: 'Achievements',
          sortable: false,
          flex: 4,
          align: 'left',
          headerAlign: 'left',
          cellClassName: 'badges-cell',
          renderCell: (params: GridRenderCellParams) => (
            <BadgeSliderAchievement badgeList={params.row.recent_badges} />
          )
        },
        {
          field: 'average_points',
          headerName: 'Average Points',
          sortable: false,
          flex: 1.5,
          align: 'right',
          headerClassName: 'header-sort-right',
          renderCell: (params: GridRenderCellParams) => formatNumber(params.value)
        }
      ];
    }

    return baseColumns;
  }, [isAttribute]);

  const removeBadgeAchievementData = async () => {
    await dispatch(
      removeBadgeAchievement()
    );
  };
  const getBadgeAchievement = async (params: any) => {
    await dispatch(getBadgeAchievementListEvents(params));
  };

  const handleRowsTable = (attribute: boolean) => {
    let rows;
    let selectedRowIds = [];
    if (attribute) {
      rows = dataBadgeAchievement.groups.data.map((learner, index) => ({
        ...learner,
        id: index
      }));
      selectedRowIds = rows
        .filter((row: groupBadgeAchievementRow) => row && row.has_group)
        .map((row: groupBadgeAchievementRow) => row.id);
    } else {
      rows = dataBadgeAchievement.rankings.data.map((learner, index) => ({
        ...learner,
        id: index
      }));
      selectedRowIds = rows
        .filter(
          (row: badgeAchievementRow) =>
            row && userCurrentCellStore && row.learner_id === userCurrentCellStore.student_id
        )
        .map((row: badgeAchievementRow) => row.id);
    }
    setSelectedRow(selectedRowIds);
    setBadgeAchievementRows(rows);
    setTotalRows(rows && rows.length);
  };

  useEffect(() => {
    getBadgeAchievement(DEFAULT_PARAMS);
    return () => {
      removeBadgeAchievementData();
    };
  }, [dispatch]);

  useEffect(() => {
    handleRowsTable(isAttribute);
  }, [dataBadgeAchievement, isAttribute]);

  const fetchMoreDataBadgeAchievement = async (dataAchievement: IDataBadgeAchievement) => {
    const { rankings, groups } = dataAchievement;
    let params = {};
    if (!isAttribute) {
      const nextOffset = ((rankings && rankings.pagination && rankings.pagination.offset) || 0) + 1;
      params = { ...DEFAULT_PARAMS, offset_rankings: nextOffset };
      if (rankings && rankings.pagination && rankings.pagination.has_more) {
        await getBadgeAchievement(params);
      }
    } else {
      const nextOffset = ((groups && groups.pagination && groups.pagination.offset) || 0) + 1;
      params = { ...DEFAULT_PARAMS, offset_groups: nextOffset };
      if (groups && groups.pagination && groups.pagination.has_more) {
        await getBadgeAchievement(params);
      }
    }
  };

  return (
    <>
      <Stack className="badge-achievement" id="badge-achievement">
        <DataTables
          tblRows={badgeAchievementRows}
          tblColumns={columns}
          totalRows={totalRows}
          sortingMode={'server'}
          autoHeight={false}
          rowSelectionModel={selectedRow}
          noRowsLabel={'No Data Available'}
          onTableScrollEnd={(data:IDataBadgeAchievement)=>fetchMoreDataBadgeAchievement(data)}
          tableScrollData={dataBadgeAchievement}
        />
      </Stack>
      <LoadingPage open={isLoading} />
    </>
  );
};

export default BadgeAchievement;
